import { useState } from 'react';
import {
  useSetSetpointMutation,
  useSetWpmOffsetMutation,
  useSetBypassZoneMutation,
  useSetWeatherLocationMutation,
} from '../api/mutations';
import type { GroupName } from '../domain';
import { WeatherLocationControl, type LatLon } from './WeatherLocationControl';

// One batched form for the house-level controls. Each row just shows its value;
// a single Save at the bottom commits every changed field via its own mutation
// (unchanged fields are not posted). Client guard rails match the server: the
// server (and device) are the real gates.
const GROUPS: GroupName[] = ['ground', 'middle_sun', 'middle_shade', 'top_sun', 'top_shade'];
const SETPOINT_MIN = 10;
const SETPOINT_MAX = 28;
const OFFSET_MIN = -5;
const OFFSET_MAX = 5;

export function SettingsControls({
  setpoint,
  wpmOffset,
  bypassGroup,
  weatherLocation,
}: {
  setpoint: number;
  wpmOffset: number;
  bypassGroup: GroupName;
  weatherLocation: LatLon | null;
}) {
  const [sp, setSp] = useState(setpoint);
  const [off, setOff] = useState(wpmOffset);
  const [grp, setGrp] = useState<GroupName>(bypassGroup);
  const [loc, setLoc] = useState<LatLon | null>(weatherLocation);

  const setpointM = useSetSetpointMutation();
  const offsetM = useSetWpmOffsetMutation();
  const bypassM = useSetBypassZoneMutation();
  const weatherM = useSetWeatherLocationMutation();

  const spValid = Number.isFinite(sp) && sp >= SETPOINT_MIN && sp <= SETPOINT_MAX;
  const offValid = Number.isFinite(off) && off >= OFFSET_MIN && off <= OFFSET_MAX;
  const locDirty = (loc?.lat ?? null) !== (weatherLocation?.lat ?? null) || (loc?.lon ?? null) !== (weatherLocation?.lon ?? null);
  const dirty = sp !== setpoint || off !== wpmOffset || grp !== bypassGroup || locDirty;
  const pending =
    setpointM.isPending || offsetM.isPending || bypassM.isPending || weatherM.isPending;
  const canSave = dirty && spValid && offValid && !pending;

  function saveAll() {
    if (sp !== setpoint && spValid) setpointM.mutate({ setpoint_c: sp });
    if (off !== wpmOffset && offValid) offsetM.mutate({ wpm_offset_c: off });
    if (grp !== bypassGroup) bypassM.mutate(grp);
    if (locDirty) weatherM.mutate({ location: loc });
  }

  const error = (setpointM.error ??
    offsetM.error ??
    bypassM.error ??
    weatherM.error) as Error | null;

  const unit = { display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' } as const;
  const field = { width: '6rem' } as const;

  return (
    <div className="card">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content auto',
          columnGap: 'var(--sp-4)',
          rowGap: 'var(--sp-3)',
          alignItems: 'center',
        }}
      >
        <label htmlFor="set-setpoint">Setpoint</label>
        <div style={unit}>
          <input
            id="set-setpoint"
            type="number"
            step="0.5"
            min={SETPOINT_MIN}
            max={SETPOINT_MAX}
            value={Number.isFinite(sp) ? sp : ''}
            onChange={(e) => setSp(parseFloat(e.target.value))}
            aria-label="house setpoint"
            style={field}
          />
          <span className="muted">°C</span>
        </div>

        <label htmlFor="set-wpm-offset">WPM offset</label>
        <div style={unit}>
          <input
            id="set-wpm-offset"
            type="number"
            step="0.1"
            min={OFFSET_MIN}
            max={OFFSET_MAX}
            value={Number.isFinite(off) ? off : ''}
            onChange={(e) => setOff(parseFloat(e.target.value))}
            aria-label="wpm offset"
            style={field}
          />
          <span className="muted">°C</span>
        </div>

        <label htmlFor="set-bypass">Bypass zone</label>
        <select
          id="set-bypass"
          value={grp}
          onChange={(e) => setGrp(e.target.value as GroupName)}
          aria-label="bypass zone"
          style={{ width: 'max-content' }}
        >
          {GROUPS.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>

        <label style={{ alignSelf: 'start' }}>Weather location</label>
        <WeatherLocationControl value={loc} onChange={setLoc} />
      </div>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <button onClick={saveAll} disabled={!canSave}>
          {pending ? 'Saving…' : 'Save'}
        </button>
        {error && (
          <span className="field-error" style={{ marginLeft: 'var(--sp-2)' }}>{error.message}</span>
        )}
      </div>
    </div>
  );
}
